/* eslint-disable @typescript-eslint/dot-notation */
import { MFE_LIST } from 'cloud-ui-scripts/scripts/constants';
import { EnvValueType, OriginsType } from './config.types';
import { ServiceOrigins } from './serviceOrigins';

const PROD_HOSTS = ['app.redislabs.com', 'cloud.redis.io'];

const EXTERNAL_HOSTS = [
  'localhost',
  'test.redislabs.com',
  'test.redis.io',
  'sm.ui.dev.redislabs.com',
  'cloud-ui.qa.redislabs.com'
];

const IS_PRODUCTION = PROD_HOSTS.some((prodHost) => window.location.hostname.startsWith(prodHost));
const IS_EXTERNAL_HOSTS = EXTERNAL_HOSTS.includes(window.location.hostname);
const RC_URL = 'https://sm.ui.dev.redislabs.com/rc';
const dynamicHost = /^([\w\-.]+).(redislabs.com|redis.io)$/.exec(window.location.hostname);
let search;
try {
  search = decodeURIComponent(window.location.search);
} catch {
  search = window.location.search;
}
const searchParams = new URLSearchParams(search);
let API_ENV: EnvValueType;

if (IS_PRODUCTION) {
  API_ENV = 'app';
} else if (process.env.VITE_APP_API_ENV) {
  API_ENV = process.env.VITE_APP_API_ENV as EnvValueType;
} else if (window.location.href.startsWith(RC_URL)) {
  API_ENV = 'app-qa.qa';
} else if (searchParams.get('api_env')) {
  API_ENV = searchParams.get('api_env') as EnvValueType;
  window.sessionStorage.setItem('api_env', API_ENV);
} else if (window.sessionStorage.getItem('api_env') !== null) {
  API_ENV = window.sessionStorage.getItem('api_env') as EnvValueType;
} else if (dynamicHost?.[1]) {
  API_ENV = dynamicHost[1] as EnvValueType;
} else {
  API_ENV = 'app-qa.qa';
}

const REDIS_IO_ENVS = ['app-gcp.qa'];

const getHostByApiEnv = (apiEnv: string) => {
  if (REDIS_IO_ENVS.includes(apiEnv)) {
    return 'redis.io';
  }

  return 'redislabs.com';
};

const defaultHost = getHostByApiEnv(API_ENV);

ServiceOrigins.DEFAULT = {
  TAGGING: `https://${API_ENV}.${defaultHost}/tagging`,
  SM: `https://${API_ENV}.${defaultHost}/api/v1`,
  MW: `https://${API_ENV}.${defaultHost}/api/v1/maintenance-window`,
  KYC: `https://${API_ENV}.${defaultHost}/kyc/api`,
  BILLING: `https://${API_ENV}.${defaultHost}/billing`,
  TGW: `https://${API_ENV}.${defaultHost}/api/v1/cloud-transit-gateway`,
  PRIVATE_LINK: `https://${API_ENV}.${defaultHost}/api/v1/cloud-resource-link`,
  DATABASE: `https://${API_ENV}.${defaultHost}/api/cloud-db-service`,
  DEDICATED_OFFERING: `https://${API_ENV}.${defaultHost}/api`,
  BILLING_PRICING: `https://${API_ENV}.${defaultHost}/cloud-billing-pricing`,
  METRICS: `https://${API_ENV}.${defaultHost}/api/cloud-metrics-service/v1`
};

ServiceOrigins.RELATIVE = {
  TAGGING: `/tagging`,
  SM: `/api/v1`,
  MW: `/api/v1/maintenance-window`,
  KYC: `/kyc/api`,
  BILLING: `/billing`,
  TGW: `/api/v1/cloud-transit-gateway`,
  PRIVATE_LINK: `/api/v1/cloud-resource-link`,
  DATABASE: `/api/cloud-db-service`,
  DEDICATED_OFFERING: '/api',
  BILLING_PRICING: '/cloud-billing-pricing',
  METRICS: '/api/cloud-metrics-service/v1'
};

let CURRENT_ORIGINS: OriginsType;

if (IS_PRODUCTION) {
  CURRENT_ORIGINS = ServiceOrigins.RELATIVE;
} else if (process.env.NODE_ENV === 'test') {
  API_ENV = 'TEST';
  CURRENT_ORIGINS = ServiceOrigins.TEST;
} else if (process.env.VITE_APP_SM_URL) {
  CURRENT_ORIGINS = ServiceOrigins.LOCAL_SM;
} else if (IS_EXTERNAL_HOSTS || searchParams.has('api_env')) {
  CURRENT_ORIGINS = ServiceOrigins.DEFAULT;
} else {
  CURRENT_ORIGINS = ServiceOrigins.RELATIVE;
}

const IS_RELATIVE_API = CURRENT_ORIGINS === ServiceOrigins.RELATIVE;

// Allow overriding the default origins with environment variables
CURRENT_ORIGINS = {
  TAGGING: process.env.VITE_APP_TAGGING_URL || CURRENT_ORIGINS.TAGGING,
  SM: process.env.VITE_APP_SM_URL || CURRENT_ORIGINS.SM,
  MW: process.env.VITE_APP_MW_URL || CURRENT_ORIGINS.MW,
  KYC: process.env.VITE_APP_KYC_URL || CURRENT_ORIGINS.KYC,
  BILLING: process.env.VITE_APP_BILLING_URL || CURRENT_ORIGINS.BILLING,
  TGW: process.env.VITE_APP_TGW_URL || CURRENT_ORIGINS.TGW,
  PRIVATE_LINK: process.env.VITE_APP_PRIVATE_LINK_URL || CURRENT_ORIGINS.PRIVATE_LINK,
  DATABASE: process.env.VITE_APP_CLOUD_DB_URL || CURRENT_ORIGINS.DATABASE,
  DEDICATED_OFFERING:
    process.env.VITE_APP_DEDICATED_OFFERING_URL || CURRENT_ORIGINS.DEDICATED_OFFERING,
  BILLING_PRICING: process.env.VITE_APP_BILLING_PRICING_URL || CURRENT_ORIGINS.BILLING_PRICING,
  METRICS: process.env.VITE_APP_METRICS_URL || CURRENT_ORIGINS.METRICS
};

const LAUNCH_DARKLY_CLIENT_ID_MAP: Record<EnvValueType, string> = {
  TEST: null,
  app: '64febc01e87179120cab3b9c',
  'app-staging.qa': '654a13d271ebcb11e9e2a83d',
  'app-sm.k8s-auto.sm-qa.qa': '666559be51ae021009e52781',
  'app-sm.k8s-aa.sm-qa.qa': '655c8ea0c3a8bb12577caa8d',
  'app-sm.k8s-ai.sm-qa.qa': '6851cf9a5f7779092ab9f369',
  'app-sm.k8s-billing-dev.sm-qa.qa': '654a1258a77d3011d7beeeaf',
  'app-sm.k8s-billing.sm-qa.qa': '654a109ef38dc41338523a3d',
  'app-sm.k8s-cloudapi.sm-qa.qa': '654a128b71ebcb11e9e2a61c',
  'app-qa.qa': '655c8fa6ea291f125eb7fe40',
  'app-sm.k8s-dev.sm-qa.qa': '654a1580a77d3011d7bef3b0',
  'app-gcp.qa': '654a15a320c646126344aead',
  'app-sm.k8s-gh.sm-qa.qa': '652bd44fc07b0d12aaf4296e',
  'app-sm.k8s-idm.sm-qa.qa': '65647b03fd742213146a5742',
  'app-sm.k8s-rdi.sm-qa.qa': '68f8933de8082b099196383a',
  'app-sm.k8s-integration.sm-qa.qa': '655cafb3ea291f125eb8381e',
  'app-sm.k8s-mw.sm-qa.qa': '65647af99ab18512d02a29ec',
  'app-sm.k8s-psc.sm-qa.qa': '654a1325bd4a5511f35f7339',
  'app-sm.k8s-rcp.sm-qa.qa': '654a134f5f2163134a67c9b8'
};

const NOVU_APPLICATION_IDENTIFIER_MAP: Record<EnvValueType, string> = {
  TEST: null,
  app: 'kiXIAcEDZwmY',
  'app-staging.qa': '',
  'app-sm.k8s-auto.sm-qa.qa': '',
  'app-sm.k8s-aa.sm-qa.qa': '',
  'app-sm.k8s-ai.sm-qa.qa': '',
  'app-sm.k8s-billing-dev.sm-qa.qa': '',
  'app-sm.k8s-billing.sm-qa.qa': '',
  'app-sm.k8s-cloudapi.sm-qa.qa': '',
  'app-qa.qa': '',
  'app-sm.k8s-dev.sm-qa.qa': '',
  'app-gcp.qa': '',
  'app-sm.k8s-gh.sm-qa.qa': '',
  'app-sm.k8s-idm.sm-qa.qa': '',
  'app-sm.k8s-rdi.sm-qa.qa': '',
  'app-sm.k8s-integration.sm-qa.qa': '',
  'app-sm.k8s-mw.sm-qa.qa': '',
  'app-sm.k8s-psc.sm-qa.qa': 'S0ziXOUs3Nu9',
  'app-sm.k8s-rcp.sm-qa.qa': ''
};

const SEGMENT_ANALYTICS_WRITE_KEY_MAP = {
  prod: 'iQVc6HOZPPsj64bmkuR0BUdWDULI3i4g',
  localDev: 'HKPvoy8bmLR9kgnQFRKbmyMKDFbq09Ap'
};

const AMPLITUDE_API_KEY_MAP = {
  prod: '55136d5e066caf15692a50b4b609f0fc',
  localDev: 'c9f337f6efbd93fd7b8fe3efb8349cc1'
};

const getAnalyticsApiKeys = () => {
  // Only load analytics in production and local dev (when debugging) to minimize Segment costs from QA environments
  if (IS_PRODUCTION) {
    return { segment: SEGMENT_ANALYTICS_WRITE_KEY_MAP.prod, amplitude: AMPLITUDE_API_KEY_MAP.prod };
  }

  const debugAnalytics = process.env.VITE_APP_DEBUG_ANALYTICS === 'true';

  if (process.env.NODE_ENV === 'development' && debugAnalytics) {
    return {
      segment: SEGMENT_ANALYTICS_WRITE_KEY_MAP.localDev,
      amplitude: AMPLITUDE_API_KEY_MAP.localDev
    };
  }

  return { segment: null, amplitude: null };
};

export const SENTRY_DSN_MAP = {
  prod: 'https://71d8870e9ac1487193fe88734296a58c@o1111372.ingest.us.sentry.io/6140641', // redis-prod project
  localDev: 'https://854aa01543ca48eca5ec5b2177d1cbce@o1111372.ingest.us.sentry.io/6177675' // redis-qa project
};

const getSentryDsn = () => {
  if (IS_PRODUCTION) {
    return SENTRY_DSN_MAP.prod;
  }

  const debugSentry = process.env.VITE_APP_DEBUG_SENTRY === 'true';

  if (process.env.NODE_ENV === 'development' && debugSentry) {
    return SENTRY_DSN_MAP.localDev;
  }

  return null;
};

const getMfeHostUrls = () => {
  return MFE_LIST.reduce((acc, mfe) => {
    let URL = `/cloud-ui-modules/${mfe.name}/entry.js`;
    const REMOTE_MFE_URL_PARAM_KEY = `${mfe.name}_mfe_remote_url`;

    // We have to make sure that we do not allow remote MFE loading in production
    if (!IS_PRODUCTION) {
      if (process.env[`VITE_APP_LOCAL_${mfe.name.toUpperCase()}_MFE`] === 'true') {
        URL = `https://test.${defaultHost}:${mfe.port}/assets/entry.js`;
      } else if (searchParams.has(REMOTE_MFE_URL_PARAM_KEY)) {
        const mfeRemoteUrl = searchParams.get(REMOTE_MFE_URL_PARAM_KEY);
        window.sessionStorage.setItem(REMOTE_MFE_URL_PARAM_KEY, mfeRemoteUrl);
        URL = mfeRemoteUrl;
      } else if (window.sessionStorage.getItem(REMOTE_MFE_URL_PARAM_KEY)) {
        URL = window.sessionStorage.getItem(REMOTE_MFE_URL_PARAM_KEY);
      } else if (IS_EXTERNAL_HOSTS) {
        URL = `https://${API_ENV}.${defaultHost}/cloud-ui-modules/${mfe.name}/entry.js`;
      }
    }

    acc[mfe.name] = URL;

    return acc;
  }, {});
};

const getRedisInsightUrl = () => {
  if (IS_PRODUCTION) {
    return `https://ri.${window.location.hostname.substring(
      window.location.hostname.indexOf('.') + 1
    )}`;
  }

  return `https://ri-${window.location.hostname}`;
};

const mfeHostUrls = getMfeHostUrls();
window['MFE_MODULES'] = mfeHostUrls;

export const Config = {
  API_ENV,
  CURRENT_ORIGINS,
  RELEASE_NUMBER: process.env.VITE_APP_RELEASE_NUMBER || 'local',
  LAUNCH_DARKLY_CLIENT_ID: IS_PRODUCTION
    ? LAUNCH_DARKLY_CLIENT_ID_MAP['app']
    : LAUNCH_DARKLY_CLIENT_ID_MAP[API_ENV] || '',
  NOVU_APPLICATION_IDENTIFIER: IS_PRODUCTION
    ? NOVU_APPLICATION_IDENTIFIER_MAP['app']
    : NOVU_APPLICATION_IDENTIFIER_MAP[API_ENV] || '',
  MFE_MODULES_URLS: mfeHostUrls,
  REDIS_INSIGHT_CLOUD_BASE_URL: getRedisInsightUrl(),
  IS_RELATIVE_API,
  IS_PRODUCTION,
  ANALYTICS_KEYS: getAnalyticsApiKeys(),
  SEGMENT_CDN_URL: IS_PRODUCTION
    ? 'https://evs.analytics.redis.io'
    : 'https://evs.analytics.app-qa.qa.redis.io',
  SENTRY_DSN: getSentryDsn()
};
